import { enablePageScroll, disablePageScroll } from 'scroll-lock';
import delay from 'delay';

import showModal from '@/utils/show_modal';
import { LOAD_MIN_MS } from '@/helpers/with_min_delay';
import silhouetteMarkup from './silhouettes';

// The way a modal is opened from a page that is not a vue application. It has two modes.
//
// With `importComponent` the modal is built here, in two phases. The first one is synchronous:
// a silhouette of the modal is stamped into the document on the click, so a client with a cold
// script cache sees the modal opening instead of nothing. The second one starts when the vue
// chunk and the modal chunk have arrived: `vue/components/modal` renders the real chrome around
// the real content and the silhouette is dropped.
//
// The silhouette is painted one task after the opening, not in the click frame: chunks already
// in the module cache resolve within the microtasks of that same task, so a ready modal takes
// over before the paint and no silhouette appears at all, while a loading one still paints it
// before the frame of the click is rendered. Once painted, the silhouette stays on screen at
// least SILHOUETTE_MINIMUM_MS before the handover — a swap a few frames after the paint reads
// as a flash, not as a modal opening.
//
// With `node` the modal is already in the document, rendered by the server, and nothing is
// built: the behaviour is bound onto the markup as it is. Use it where the content is worth
// keeping on the server — a form that posts to a route, prices formatted by a view object.
//
// `props` are handed to the content component as vnode props, so vue's render-function
// convention applies: a key named `on<Event>` ('onDetails:click') subscribes to the emit of that
// event — there is no separate option for event handlers.
//
// Inside a vue application there is nothing to wait for and no silhouette: use the component.

// a painted silhouette is kept on screen as long as any other load indicator of the project
// before the chrome replaces it
export const SILHOUETTE_MINIMUM_MS = LOAD_MIN_MS;

export default function makeModal(options) {
  if (Boolean(options.node) === Boolean(options.importComponent)) {
    throw new Error('makeModal takes exactly one of node or importComponent');
  }

  return options.node ? serverModal(options) : vueModal(options);
}

function serverModal({ node, trigger, onShow, onHide, isDestroyOnHide = false }) {
  const $modal = $(node);
  const $trigger = trigger ? $(trigger) : undefined;

  const modal = showModal({
    $modal,
    $trigger,
    isHiddenCheck: () => !$modal.hasClass('is-active'),
    show() {
      disablePageScroll();
      $modal.addClass('is-active');
      if (onShow) { onShow(); }
    },
    hide() {
      enablePageScroll();
      $modal.removeClass('is-active');
      if (onHide) { onHide(); }
      if (isDestroyOnHide) { handle.destroy(); }
    }
  });

  $modal.find('.modal-shade').on('click', modal.hide);
  $modal.find('.modal-close').on('click', modal.hide);

  const handle = {
    show() {
      modal.show();
      return handle;
    },
    hide() {
      modal.hide();
      return handle;
    },
    destroy() {
      modal.destroy();
      $modal.remove();
    }
  };

  return handle;
}

function vueModal({
  trigger,
  cssClass,
  silhouette = 'call_lead',
  importComponent,
  props = null,
  isCloseOnSubmit = false,
  isDestroyOnHide = false,
  onShow,
  onHide
}) {
  const $trigger = trigger ? $(trigger) : undefined;
  // a modal that opens on its own, rather than in answer to a click, has nothing to stand in
  // for — it simply appears once it is ready, so such a caller passes no silhouette
  const $silhouette = silhouette ?
    $(silhouetteMarkup({ preset: silhouette, cssClass })) :
    null;

  let isActive = false;
  let isVuePhase = false;
  let isDestroyed = false;
  let activeRef = null;
  let vueApp = null;
  let hostNode = null;
  let silhouettePaintTimer = null;
  let silhouettePaintedAt = null;

  $silhouette?.appendTo(document.body);

  const silhouetteModal = $silhouette && showModal({
    $modal: $silhouette,
    $trigger,
    isHiddenCheck: () => !isActive,
    show() {
      disablePageScroll();
      // the paint is deferred by a task, so the chunks get their microtasks to win the race:
      // when they are already in the module cache the handover cancels this timer and the
      // silhouette never reaches the screen
      silhouettePaintTimer ??= setTimeout(() => {
        silhouettePaintTimer = null;
        silhouettePaintedAt = Date.now();
        $silhouette.addClass('is-active');
      });
      activate(true);
    },
    hide() {
      enablePageScroll();
      clearTimeout(silhouettePaintTimer);
      silhouettePaintTimer = null;
      silhouettePaintedAt = null;
      $silhouette.removeClass('is-active');
      activate(false);
    }
  });

  // the chrome binds its own shade and close once it mounts (`vue/components/modal`); until the
  // handover the silhouette needs the same binding — the shade covers the viewport from inside
  // `$modal`, so the outside-click of `show_modal` can never fire on it
  if (silhouetteModal) {
    $silhouette.find('.modal-shade').on('click', silhouetteModal.hide);
    $silhouette.find('.modal-close').on('click', silhouetteModal.hide);
  }

  const handle = {
    show() {
      if (isVuePhase) {
        activeRef.value = true;
      } else if (silhouetteModal) {
        silhouetteModal.show();
      } else {
        // wo a silhouette there is nothing to show yet: the chrome opens itself when it mounts,
        // and the opening is reported now, so the mount does not report it a second time
        activate(true);
      }

      return handle;
    },
    hide() {
      if (isVuePhase) {
        activeRef.value = false;
      } else if (silhouetteModal) {
        silhouetteModal.hide();
      } else {
        activate(false);
      }

      return handle;
    },
    destroy() {
      isDestroyed = true;
      clearTimeout(silhouettePaintTimer);
      silhouetteModal?.destroy();
      $silhouette?.remove();

      if (vueApp) {
        vueApp.unmount();
        hostNode.remove();
        vueApp = null;
      }
    }
  };

  renderModal();

  return handle;

  async function renderModal() {
    const [{ createApp, ref, h }, { default: Component }, { default: Modal }] = await Promise.all([
      import(/* webpackChunkName: "vue" */ 'vue'),
      importComponent(),
      import('@/vue/components/modal')
    ]);

    if (silhouettePaintedAt) {
      const remaining = SILHOUETTE_MINIMUM_MS - (Date.now() - silhouettePaintedAt);

      if (remaining > 0) { await delay(remaining); }
    }

    // the modal may well have been destroyed while the chunks were on their way — a lead form
    // that submits and navigates away, a turbolinks visit right after the click. Mounting now
    // would put a modal nobody holds a handle to onto the page
    if (isDestroyed) { return; }

    clearTimeout(silhouettePaintTimer);

    activeRef = ref(isActive);
    hostNode = document.createElement('div');
    document.body.appendChild(hostNode);

    vueApp = createApp({
      setup: () => () => h(
        Modal,
        {
          isActive: activeRef.value,
          cssClass,
          triggerNode: $trigger ? $trigger[0] : null,
          'onModal:show': handleShow,
          'onModal:hide': handleHide
        },
        { default: () => h(Component, contentProps()) }
      )
    });
    vueApp.mount(hostNode);
    isVuePhase = true;

    // the component locked the page scroll on its own when it mounted already open, so the lock
    // the silhouette had taken is released — scroll-lock counts them
    if (isActive && silhouetteModal) { enablePageScroll(); }

    silhouetteModal?.destroy();
    $silhouette?.remove();
  }

  function contentProps() {
    const eventHandlers = { 'onModal:close': handle.hide };

    if (isCloseOnSubmit) {
      eventHandlers['onModal:submit'] = handle.hide;
    }

    return { ...(props || $trigger?.data()), ...eventHandlers };
  }

  function handleShow() {
    activeRef.value = true;
    activate(true);
  }

  function handleHide() {
    activeRef.value = false;
    activate(false);
  }

  function activate(isOn) {
    // the chrome mounts already open and says so, repeating the opening the silhouette has
    // already reported — the client callbacks must fire once per opening, not once per phase
    if (isOn === isActive) { return; }

    isActive = isOn;

    if (isOn) {
      if (onShow) { onShow(); }
    } else {
      if (onHide) { onHide(); }
      if (isDestroyOnHide) { handle.destroy(); }
    }
  }
}
